import STATUS from 'app/redux/features/pad-initialization/constants/status.constant.js';

export function generateCode() {
  const alpha = 'abcdefghijklmnopqrstuvwxyz';
  const digit = () => Math.floor(Math.random() * 10);
  const firstLetter = alpha[Math.floor(Math.random() * alpha.length)];
  return `${firstLetter}${digit()}${digit()}${digit()}`;
}

/**
 * Default structure for an Event in the calendar visit schedule.
 * Represents a scheduled visit with a professional and datetime.
 */
export const DEFAULT_EVENT = {
  key: generateCode(),
  professionalId: null,
  professional: {},
  dateTime: null,
  padPrescriptionIds: []
};

/**
 * Factory function that creates an Event instance.
 * Ensures all event objects have the correct structure.
 *
 * @param {Object} data - Event data
 * @param {number} data.professionalId - ID of the professional
 * @param {Object} data.professional - Professional object (name, etc.)
 * @param {string} data.dateTime - ISO datetime string
 * @param {Array<number>} data.padPrescriptionIds - Array of prescription IDs
 * @returns {Object} A new Event instance
 */
export const createEvent = data => {
  if (!data) {
    return { ...DEFAULT_EVENT };
  }

  return {
    key: data.key || generateCode(),
    professionalId: data.professionalId || null,
    professional: data.professional || {},
    dateTime: data.dateTime || null,
    padPrescriptionIds: data.padPrescriptionIds || []
  };
};

/**
 * Validates an event object structure.
 *
 * @param {Object} event - Event to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidEvent = event => {
  if (!event || typeof event !== 'object') {
    return false;
  }

  const hasProfessionalId = event.professionalId !== undefined && event.professionalId !== null;
  const hasDateTime = event.dateTime !== undefined && event.dateTime !== null;
  const hasProfessional = event.professional !== undefined;

  return hasProfessionalId && hasDateTime && hasProfessional;
};

/**
 * Default structure for a CalendarVisitSchedule.
 * Centralizes the schema definition to ensure consistency across the application.
 */
export const DEFAULT_CALENDAR_VISIT_SCHEDULE = {
  id: null,
  padVisitId: null,
  padVisit: null,
  previousEvents: [],
  events: [],
  automaticRepeat: false,
  prescriptionEventsSchedule: [],
  prescriptionLoadSchedule: [],
  dailyAutomaticRepeat: false,
  canStepFoward: false,
  status: STATUS.IDLE,
  error: null
};

/**
 * Factory function that creates a CalendarVisitSchedule instance from different sources.
 * Handles both padVisit objects and prescription schedule objects.
 *
 * @param {Object} source - The source object (padVisit or prescription schedule)
 * @returns {Object} A new CalendarVisitSchedule instance
 */
export const createCalendarVisitSchedule = source => {
  if (!source) {
    return { ...DEFAULT_CALENDAR_VISIT_SCHEDULE };
  }

  // Determine the source type and extract accordingly
  const isPadVisit = source.padVisitId !== undefined || source.id !== undefined;
  const padVisit = isPadVisit ? source.padVisit || source : source.padVisit;
  const padVisitId =
    source.padVisitId || (padVisit && padVisit.id) || (source && source.id) || null;
  const id = source.id || padVisitId;

  return {
    id,
    padVisitId,
    padVisit,
    previousEvents: source.previousEvents || (padVisit && padVisit.previousEvents) || [],
    events: Array.isArray(source.events) ? source.events.map(createEvent) : [],
    prescriptionEventsSchedule: [],
    prescriptionLoadSchedule: source.prescriptionLoadSchedule || [],
    automaticRepeat: source.automaticRepeat || false,
    dailyAutomaticRepeat: source.dailyAutomaticRepeat || false,
    canStepFoward: false,
    status: STATUS.IDLE,
    error: null
  };
};
